import { useEffect, useContext, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UserContext from "../assets/user_context";

// Debug logging - set to true to enable verbose logging
const DEBUG_LOGGING = false;
const debugLog = (...args) => DEBUG_LOGGING && debugLog(...args);

/**
 * OrgUrlManager - Handles URL-based organization management
 * Must be rendered inside Router context
 */
const OrgUrlManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userData, org, setOrg, selectOrgOpen, setSelectOrgOpen, controlSessions } =
    useContext(UserContext);

  // Ref to prevent infinite loop between URL updates and URL reading
  const isUpdatingUrlRef = useRef(false);
  // Ref to track if the org change came from URL (to prevent updating URL again)
  const orgChangeFromUrlRef = useRef(false);

  // Helper functions for URL org management
  const getOrgFromUrl = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get("org");
  }, [location.search]);

  const updateUrlWithOrg = useCallback(
    (orgId) => {
      if (!orgId) return;

      const urlParams = new URLSearchParams(location.search);
      const currentOrgInUrl = urlParams.get("org");

      if (currentOrgInUrl !== orgId) {
        debugLog(`Updating URL with org: ${orgId}`);
        isUpdatingUrlRef.current = true;
        urlParams.set("org", orgId);
        const newUrl = `${location.pathname}?${urlParams.toString()}`;
        // Use replace to avoid adding to browser history for every org change
        navigate(newUrl, { replace: true });

        // Clear the flag after a brief delay to allow navigation to complete
        setTimeout(() => {
          isUpdatingUrlRef.current = false;
        }, 100);
      }
    },
    [location.search, location.pathname, navigate],
  );

  const userHasAccessToOrg = useCallback((orgId, userOrgs) => {
    const normalAccess = userOrgs && userOrgs.some((org) => org.orgID === orgId);
    const controlAccess = controlSessions.some((s) => s.orgId === orgId);
    return normalAccess || controlAccess;
  }, [controlSessions]);

  // Update URL when org changes in context
  useEffect(() => {
    if (org) {
      // Only update URL if the org change didn't come from URL itself
      if (!orgChangeFromUrlRef.current) {
        // Only update URL if it doesn't already match the current org
        const currentUrlOrgId = getOrgFromUrl();
        if (currentUrlOrgId !== org) {
          updateUrlWithOrg(org);
        }
      } else {
        // Clear the flag after handling URL-based org change
        orgChangeFromUrlRef.current = false;
      }
    }
  }, [org, updateUrlWithOrg, getOrgFromUrl]);

  // Check URL for org on load and set if user has access
  useEffect(() => {
    // Skip if we're currently updating the URL ourselves to prevent infinite loop
    if (isUpdatingUrlRef.current) {
      debugLog("Skipping URL check - currently updating URL");
      return;
    }

    if (user && userData?.orgs) {
      const urlOrgId = getOrgFromUrl();

      if (urlOrgId && userHasAccessToOrg(urlOrgId, userData.orgs)) {
        // URL has org and user has access - always prioritize URL
        if (urlOrgId !== org) {
          debugLog(`Setting org from URL: ${urlOrgId}`);
          orgChangeFromUrlRef.current = true;
          setOrg(urlOrgId);
          // Close modal if it's open since we found a valid org in URL
          if (selectOrgOpen) {
            setSelectOrgOpen(false);
          }
        }
      } else if (urlOrgId && !userHasAccessToOrg(urlOrgId, userData.orgs)) {
        // URL has org but user doesn't have access - remove from URL
        debugLog(`User doesn't have access to org in URL: ${urlOrgId}`);
        isUpdatingUrlRef.current = true;
        const urlParams = new URLSearchParams(location.search);
        urlParams.delete("org");
        const newUrl = `${location.pathname}${
          urlParams.toString() ? "?" + urlParams.toString() : ""
        }`;
        navigate(newUrl, { replace: true });

        // Clear the flag after navigation
        setTimeout(() => {
          isUpdatingUrlRef.current = false;
        }, 100);
      }
    }
  }, [
    user,
    userData,
    org,
    getOrgFromUrl,
    userHasAccessToOrg,
    setOrg,
    selectOrgOpen,
    setSelectOrgOpen,
    navigate,
    location.pathname,
    location.search,
  ]);

  // This component doesn't render anything
  return null;
};

export default OrgUrlManager;
